import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAllWarehouses, getWarehouseFilters } from "@/helpers/apis";
import { ColorInsightTags } from "@/components/tags";
import { AUTO_TUNE_MAP, GET_WAREHOUSE, GET_WAREHOUSE_FILTERS } from "../constants";
import { useAppState } from "@/modules/app/useAppContext";
import { formatNumber } from "@/uiCore";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import DataTable from "@/components/tables/DataTable";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { getInstanceName, roundToTwoDecimalPlaces } from "@/helpers/utils";
import { ClockRotate, Resize, WavePulse } from "@assets/icons";
import { Tooltip, Typography, Combobox, ComboboxOption } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import AutoSavingsStatus from "@/components/AutoSavingsStatus";
import { Paginate } from "@/components/paginate";
import { FilterType } from "@/components/tables/types";
import { WarehouseItem } from "../types";
import { AUTO_TUNE_STATUS } from "../constants";
import { useWarehouseFilters } from "../hooks/useWarehouseFilters";
import { FilterApplicableFor } from "@/components/tags/types";
import SpacesRedirectionButton from "@/components/spaces/SpacesRedirectionButton";
import { SPACES_TAG_MAPPING } from "@/helpers/spacesConstants";
import WarehouseDownload from "./WarehouseDownload";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";

const WarehouseListWithFilter = () => {
  const { currency, currencySymbol, datastores } = useAppState();
  const { updateSearchParamsByKey, deleteSearchParamsByKeyValue } = useFiltersBySearchParams();

  // Use pagination hook for context-based pagination
  const { page, pageSize, setPage, setPageSize: updatePageSize, resetPage } = useTablePagination();
  const { getFilterValue } = useDatatableContext();

  // Use new filters implementation
  const { filterParams, isInitialized } = useWarehouseFilters();
  const warehouseFilter = useMemo(
    () => getFilterValue<string[]>("warehouse") || [],
    [getFilterValue]
  );
  const costFilterMode = useMemo(
    () => (getFilterValue<string>("costFilterMode") || "lt") as "lt" | "bt" | "gt",
    [getFilterValue]
  );

  const { data: filterOptions, isFetching: isfiltersLoading } = useQuery({
    queryKey: [GET_WAREHOUSE_FILTERS],
    queryFn: () => getWarehouseFilters(),
  });

  const { data: warehouses, isFetching: isQueriesLoading } = useQuery({
    queryKey: [GET_WAREHOUSE, filterParams],
    queryFn: () => getAllWarehouses(filterParams),
    enabled: isInitialized, // Only fetch when URL sync is complete
  });

  const handlePageChange = (value: number) => {
    setPage(value);
  };

  const getOptions = (data?: string[]) => {
    if (!data || data.length === 0) {
      return [];
    }
    return data.map(item => ({ label: item, value: item }));
  };

  const autoTuneFilterOptions = [
    { label: "On", value: AUTO_TUNE_MAP["auto-tune-enabled"].value },
    { label: "Off", value: AUTO_TUNE_MAP["auto-tune-disabled"].value },
  ];

  // Create warehouse search options from filterOptions
  const warehouseSearchOptions = useMemo((): ComboboxOption[] => {
    if (!filterOptions?.warehouse_names) return [];
    return filterOptions.warehouse_names.map(name => ({
      label: name,
      value: name,
    }));
  }, [filterOptions?.warehouse_names]);

  // Handle warehouse search selection
  const handleWarehouseSearch = (value: string | string[]) => {
    // If a value is selected, set only the warehouse filter
    if (value && typeof value === "string") {
      updateSearchParamsByKey({
        warehouse: value,
        page: undefined,
      });
    } else {
      deleteSearchParamsByKeyValue({ warehouse: undefined });
    }
  };

  return (
    <>
      <Stack direction={"row"} justify={"between"} className="al-mb-4">
        <Stack direction={"row"} align={"center"} gap={"md"}>
          <Typography variant="section-heading">Warehouses</Typography>
          <SpacesRedirectionButton tag={SPACES_TAG_MAPPING.WAREHOUSE} />
        </Stack>
        <Stack align={"center"} gap={"md"}>
          <div className="al-relative">
            <div className="">
              <Combobox
                options={warehouseSearchOptions}
                value={warehouseFilter.length === 1 ? (warehouseFilter[0] as string) : ""}
                onChange={handleWarehouseSearch}
                placeholder="Enter warehouse name"
                searchPlaceholder="Enter warehouse name"
                multiSelect={false}
                searchMode
              />
            </div>
          </div>
          <WarehouseDownload />
          <div id="columnpicker-container" />
        </Stack>
      </Stack>
      <DataTable<WarehouseItem>
        getRowUrl={warehouse => `/warehouse/${warehouse.instance_id}/${warehouse.warehouse_rk}`}
        getRowState={warehouse => ({ entityName: warehouse.warehouse_name })}
        columnPickerProps={{ buttonProps: { variant: "outline" } }}
        applicableFor={FilterApplicableFor.WAREHOUSES}
        minWidth={2400}
        portals={{
          filter: "filter-container",
          savedFilters: "saved-filters-container",
        }}
        header={[
          {
            id: 1,
            label: "Warehouse name",
            uid: "warehouse_name",
            width: "12%",
            filter: {
              filterType: FilterType.DROPDOWN,
              options: getOptions(filterOptions?.warehouse_names),
              searchKey: "warehouse",
            },
            render: item => (
              <Stack direction={"column"}>
                <span className="al-text-brand al-break-all al-cursor-pointer">
                  {item.warehouse_name}
                </span>
                <Stack gap={"sm"} align={"center"} className={"al-mt-2"}>
                  {item.is_auto_tune_enabled !== null &&
                    item.is_auto_tune_enabled === "auto-tune-enabled" && (
                      <Tooltip title="Auto Tune Enabled">
                        <div
                          className={"al-p-2 al-rounded-full al-bg-bg-brand al-text-brand"}
                          aria-label="Auto Tune Enabled"
                        >
                          <WavePulse />
                        </div>
                      </Tooltip>
                    )}
                  {item.auto_resize_mode !== null && item.auto_resize_mode === "manual_resize" && (
                    <Tooltip title="Approval Resize Enabled">
                      <div
                        className={"al-p-2 al-rounded-full al-bg-bg-purple al-text-action-purple"}
                        aria-label="Approval Resize Enabled"
                      >
                        <ClockRotate />
                      </div>
                    </Tooltip>
                  )}
                  {item.auto_resize_mode !== null && item.auto_resize_mode === "auto_resize" && (
                    <Tooltip title="Real-Time Resize Enabled">
                      <div
                        className={"al-p-2 al-rounded-full al-bg-bg-orange al-text-action-orange"}
                        aria-label="Real-Time Resize Enabled"
                      >
                        <Resize />
                      </div>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            ),
          },
          {
            id: 2,
            label: "Cost",
            uid: "total_cost",
            width: "5%",
            filter: {
              filterType: FilterType.TEXT,
              placeHolder: `Specify cost in ${currencySymbol}`,
              searchKey: "cost",
              label: "Cost",
              filterMode: costFilterMode,
              filterModeSearchKey: "costFilterMode",
            },
            sort: {
              sortSearchKey: "sortAttribute",
            },
            render: item => (
              <div className={"al-break-all"}>{formatNumber(item.total_cost, { currency })}</div>
            ),
          },
          {
            id: 3,
            label: "Size",
            uid: "warehouse_size",
            width: "7%",
            filter: {
              filterType: FilterType.DROPDOWN,
              options: getOptions(filterOptions?.warehouse_sizes),
              searchKey: "size",
            },
            render: item => <div className={"al-break-all"}>{item.warehouse_size}</div>,
          },
          {
            id: 4,
            label: "90% exec. time",
            uid: "execution_time_p90",
            width: "4%",
            sort: {
              sortSearchKey: "sortAttribute",
            },
            render: item => <div className={"al-break-all"}>{item.execution_time_p90}</div>,
          },
          {
            id: 5,
            label: "Opportunities",
            uid: "opportunities",
            width: "10%",
            filter: {
              filterType: FilterType.DROPDOWN,
              options: getOptions(filterOptions?.opportunities),
              searchKey: "opportunities",
            },
            render: item => (
              <Stack className={"al-w-full al-relative al-z-10"}>
                <ColorInsightTags tags={item?.opportunities || []} showMore />
              </Stack>
            ),
          },
          {
            id: 6,
            label: "Insights",
            uid: "tags",
            width: "12%",
            filter: {
              filterType: FilterType.DROPDOWN,
              options: getOptions(filterOptions?.tags),
              searchKey: "tags",
            },
            render: item => (
              <Stack className={"al-w-full al-relative al-z-10"}>
                <ColorInsightTags tags={item?.tags || []} showMore />
              </Stack>
            ),
          },
          {
            id: 7,
            label: "Auto Tune Savings",
            uid: "auto_tune_savings",
            width: "8%",
            filter: {
              filterType: FilterType.DROPDOWN_SINGLE,
              options: autoTuneFilterOptions,
              searchKey: "autotune",
              label: "Auto tune status",
            },
            tooltipInfo: "Sorting by annualized savings",
            sort: {
              sortSearchKey: "sortAttribute",
            },
            render: item => (
              <div className="al-pr-5">
                <Stack
                  direction="column"
                  gap="xs"
                  justify="center"
                  align="start"
                  className="al-mb-2"
                >
                  {item.is_auto_tune_enabled === "auto-tune-disabled" && (
                    <Typography
                      variant="body"
                      className="al-text-slate-800 al-text-sm al-font-normal al-leading-[18px]"
                    >
                      Estimated (annualized):{" "}
                      <span className="al-text-red-600">
                        {formatNumber(item.estimated_annualized_savings, {
                          currency,
                        })}
                      </span>
                    </Typography>
                  )}
                  {item.is_auto_tune_enabled === "auto-tune-enabled" && (
                    <>
                      <Typography
                        variant="body"
                        className="al-text-slate-800 al-text-sm al-font-normal al-leading-[18px]"
                      >
                        Realized:{" "}
                        {formatNumber(item.realized_savings, {
                          currency,
                        })}
                      </Typography>
                      <Typography
                        variant="body"
                        className="al-text-slate-800 al-text-sm al-font-normal al-leading-[18px]"
                      >
                        Annualized:{" "}
                        {formatNumber(item.estimated_annualized_savings, {
                          currency,
                        })}
                      </Typography>
                    </>
                  )}
                </Stack>
                <AutoSavingsStatus autoTuneStatus={item.is_auto_tune_enabled as AUTO_TUNE_STATUS} />
              </div>
            ),
          },
          {
            id: 8,
            label: "Idle time",
            uid: "idle_time",
            width: "6%",
            sort: {
              sortSearchKey: "sortAttribute",
            },
            render: item => (
              <div className={"al-break-all"}>
                <div>{item.idle_time}</div>
                <div>{item.idle_percentage}</div>
              </div>
            ),
          },
          {
            id: 9,
            label: "Auto suspend",
            uid: "auto_suspend",
            width: "5%",
            render: item => (
              <div className={"al-break-all"}>{roundToTwoDecimalPlaces(item.auto_suspend)} sec</div>
            ),
          },
          {
            id: 10,
            label: "Med. queuing time",
            uid: "median_queuing_time",
            width: "5%",
            sort: {
              sortSearchKey: "sortAttribute",
            },
            render: item => <div className={"al-break-all"}>{item.median_queuing_time}</div>,
          },
          {
            id: 11,
            label: "Med. elapsed time",
            uid: "median_total_elapsed_time",
            width: "5%",
            sort: {
              sortSearchKey: "sortAttribute",
            },
            render: item => <div className={"al-break-all"}>{item.median_total_elapsed_time}</div>,
          },
          {
            id: 12,
            label: "Active time",
            uid: "total_active_time",
            width: "5%",
            sort: {
              sortSearchKey: "sortAttribute",
            },
            render: item => <div className={"al-break-all"}>{item.total_active_time}</div>,
          },
          {
            id: 13,
            label: "Snowflake Tags",
            uid: "snowflake_tags",
            width: "10%",
            filter: {
              filterType: FilterType.DROPDOWN,
              options: getOptions(filterOptions?.snowflake_tags),
              searchKey: "snowflake_tags",
            },
            render: item => (
              <Stack className={"al-w-full al-relative al-z-10"}>
                <ColorInsightTags tags={item?.snowflake_tags || []} showMore />
              </Stack>
            ),
          },
          {
            id: 14,
            width: "8%",
            label: "Instance",
            uid: "instance_id",
            filter: {
              filterType: FilterType.DROPDOWN,
              searchKey: "instance",
              options: (datastores || []).map(instance => ({
                label: instance.name,
                value: (instance.instance_id || "").toString(),
              })),
            },
            render: item => (
              <div className={"al-break-all"}>
                {getInstanceName(item.instance_id, datastores || [])}
              </div>
            ),
          },
        ]}
        data={warehouses || { items: [] }}
        columnPickerPortal="columnpicker-container"
        resetPage={resetPage}
        isLoading={isfiltersLoading || isQueriesLoading}
      />
      <Paginate
        itemCount={warehouses?.total || 0}
        page={page}
        pageSize={pageSize}
        numPages={warehouses?.pages || 1}
        onPageClick={(value: number) => handlePageChange(value)}
        onPageSizeChange={updatePageSize}
        showPageSize
        isLoading={isfiltersLoading || isQueriesLoading}
      />
    </>
  );
};

export default WarehouseListWithFilter;
